import ParserBase from "./parser-base";
import ValueMapper from "../common/value-mapper";
import DateWrapper from "../common/date-wrapper";

/**
 * 加工、出荷情報を表示データに変換する
 */
class Shipping extends ParserBase {
  /**
   * 採取、海域区分を表示データに変換する
   */
  parseHarvestSeaArea(value) {
    return ValueMapper.map({
      1: "広島湾西部",
      2: "広島湾北部",
      3: "広島湾中部",
      4: "広島湾南部",
      5: "呉湾",
      6: "広湾",
      7: "三津湾",
      8: "広島県東部",
    }).getOrElse(value, "-");
  }

  /**
   * ひもの色を表示データに変換する
   */
  parseCordColor(value) {
    return ValueMapper.map({
      1: "黄",
      2: "赤",
      3: "橙",
      4: "白",
      5: "青",
      6: "緑",
      7: "紫",
      8: "黒",
    }).getOrElse(value, "-");
  }

  /**
   * 冷却方法を表示データに変換する
   */
  parseCoolingMethod(valuePet, valueSeaWater, valueOther) {
    return ValueMapper.join(
      [
        { value: "ペットボトル", condition: valuePet },
        { value: "海水氷", condition: valueSeaWater },
        valueOther,
      ],
      "<br />"
    ).getOrElse("-");
  }

  /**
   * 出荷重量を表示データに変換する
   */
  totalShippingWeight(shippingDestinations) {
    return ValueMapper.join(
      [
        ValueMapper.sum(
          shippingDestinations.map(
            (item) => item.shipping_destination.weight_kg
          ),
          -1
        ).present((total) => `${total} kg`),
        ValueMapper.sum(
          shippingDestinations.map(
            (item) => item.shipping_destination.weight_can
          ),
          -1
        ).present((total) => `${total} 缶`),
      ],
      "<br />"
    ).getOrElse("-");
  }

  /**
   * 殻付き出荷個数を表示データに変換する
   */
  totalShippingNum(shippingDestinations) {
    return ValueMapper.join(
      [
        ValueMapper.sum(
          shippingDestinations.map(
            (item) => "num_s_size" in item.shipping_destination ? item.shipping_destination.num_s_size : 0
          ),
          -1
        ).present((total) => `Sサイズ ${total} 個`),
        ValueMapper.sum(
          shippingDestinations.map(
            (item) => "num_m_size" in item.shipping_destination ? item.shipping_destination.num_m_size : 0
          ),
          -1
        ).present((total) => `Mサイズ ${total} 個`),
        ValueMapper.sum(
          shippingDestinations.map(
            (item) => "num_l_size" in item.shipping_destination ? item.shipping_destination.num_l_size : 0
          ),
          -1
        ).present((total) => `Lサイズ ${total} 個`),
        ValueMapper.sum(
          shippingDestinations.map(
            (item) => "num_2l_size" in item.shipping_destination ? item.shipping_destination.num_2l_size : 0
          ),
          -1
        ).present((total) => `2Lサイズ ${total} 個`),
      ],
      "<br />"
    ).getOrElse("-");
  }

  /**
   * 区分を表示データに変換する
   */
  parseDoublingRate(value) {
    return ValueMapper.map({
      1: "通常（二倍体）",
      2: "三倍体",
    }).getOrElse(value, "-");
  }

  dispDoublingRate(shippingDestinations) {
    return ValueMapper.join(
      shippingDestinations.map(
        (item) => this.parseDoublingRate(item.shipping_destination.doubling_rate)), "-").getOrElse( "-");
  }
  /**
   * 受信したHACCP情報を表示データに変換する
   * @param {受信したHACCP情報} data
   * @returns 表示データ
   */
  contentsParse(data) {
    return [
      {
        title: "水揚げ日",
        message: new DateWrapper(data.unloading_date).format.localizedDate,
      },
      {
        title: "採取海域区分",
        message: this.parseHarvestSeaArea(data.harvest_sea_area),
      },
      {
        title: "結束ヒモの色",
        message: this.parseCordColor(data.cord_color),
      },
      {
        title: "むき身作業中の冷却方法",
        message: this.parseCoolingMethod(
          data.cooling_method_pet,
          data.cooling_method_seawater,
          data.cooling_method_other
        ),
      },
      {
        title: "出荷前冷蔵",
        message: ValueMapper.map({
          1: "なし",
          2: "冷蔵庫",
        }).getOrElse(data.refrigerate, "-"),
      },
      {
        title: "出荷先",
        message: ValueMapper.join(
          data.shipping_destinations.map((item) => {
            return item.shipping_destination.name;
          }),
          "<br />"
        ).getOrElse("-"),
      },
      {
        title: "合計出荷重量",
        message: this.totalShippingWeight(data.shipping_destinations),
      },
      {
        title: "殻付き合計出荷個数",
        message: this.totalShippingNum(data.shipping_destinations),
      },
      {
        title: "区分",
        message: this.dispDoublingRate(data.shipping_destinations),
      },
      {
        title: "出荷ラベル",
        message: ValueMapper.join(
          data.shipping_labels.map((item) => {
            const indent = "&nbsp;&nbsp;&nbsp;&nbsp;";
            const label = item.shipping_label;
            const expiration_date = new DateWrapper(label.expiration_date)
              .format.localizedDate;
            const name = ValueMapper.map({
              1: "生食用",
              2: "加熱用",
            }).getOrElse(label.name, "-");
            return ValueMapper.join(
              [
                `${name}`,
                `加工者：${label.processing_person}`,
                `保存方法（温度）：${label.preservation_temperature}℃`,
                `消費期限：${expiration_date}`,
              ],
              `<br />${indent}`
            ).getOrElse("-");
          }),
          "<br />"
        ).getOrElse("-"),
      },
    ];
  }
}

export default new Shipping({
  type: "object",
  properties: {
    type: { type: "string", enum: ["SHIPPING"] },
    date: { type: "string", format: "date" },
    unloading_date: { type: "string" },
    harvest_sea_area: { type: "number", enum: [1, 2, 3, 4, 5, 6, 7, 8] },
    cord_color: { type: "number", enum: [1, 2, 3, 4, 5, 6, 7, 8] },
    cooling_method_pet: { type: "boolean" },
    cooling_method_seawater: { type: "boolean" },
    cooling_method_other: { type: "string" },
    refrigerate: { type: "number", enum: [1, 2] },
    refrigerate_temperature: { type: "number" },
    shipping_destinations: {
      type: "array",
      items: {
        type: "object",
        properties: {
          shipping_destination: {
            type: "object",
            properties: {
              name: { type: "string" },
              weight_kg: { type: "number" },
              weight_can: { type: "number" },
              num_s_size: { type: "number" },
              num_m_size: { type: "number" },
              num_l_size: { type: "number" },
              num_2l_size: { type: "number" },
              doubling_rate: { type: "number", enum: [1, 2]},
            },
            required: ["name", "weight_kg", "weight_can"],
          },
        },
        required: ["shipping_destination"],
      },
    },
    shipping_labels: {
      type: "array",
      items: {
        type: "object",
        properties: {
          shipping_label: {
            type: "object",
            properties: {
              name: { type: "number", enum: [1, 2] },
              expiration_date: { type: "string" },
              preservation_temperature: { type: "number" },
              processing_person: { type: "string" },
            },
            required: [
              "name",
              "expiration_date",
              "preservation_temperature",
              "processing_person",
            ],
          },
        },
        required: ["shipping_label"],
      },
    },
  },
  required: [
    "type",
    "date",
    "unloading_date",
    "harvest_sea_area",
    "cord_color",
    "cooling_method_pet",
    "cooling_method_seawater",
    "cooling_method_other",
    "refrigerate",
    "refrigerate_temperature",
    "shipping_destinations",
    "shipping_labels",
  ],
});
